import { merge } from 'lodash-es';
import { __classPrivateFieldGet } from 'tslib';

var ElementCategory;
(function (ElementCategory) {
    ElementCategory["LineCharts"] = "Line charts";
    ElementCategory["BarCharts"] = "Bar charts";
    ElementCategory["CircleCharts"] = "Circle charts";
    ElementCategory["SingleValueCharts"] = "Single value charts";
    ElementCategory["Tables"] = "Tables";
    ElementCategory["Others"] = "Others";
})(ElementCategory || (ElementCategory = {}));
/**
 * attaching metadata via reflect-metadata either doesn't seem to work (for angular components),
 * or I don't fully understand how to do it.
 * Workaround: collect data on a window object
 */
function angularPropertiesComponents() {
    if (!window['__dbbAngularPropertiesComponents']) {
        window['__dbbAngularPropertiesComponents'] = new Array();
    }
    return window['__dbbAngularPropertiesComponents'];
}
function angularDefinitionProviders() {
    if (!window['__dbbAngularDefinitionProviders']) {
        window['__dbbAngularDefinitionProviders'] = new Map();
    }
    return window['__dbbAngularDefinitionProviders'];
}
function angularDashboardElementComponents() {
    if (!window['__dbbAngularDashboardElementComponents']) {
        window['__dbbAngularDashboardElementComponents'] = new Array();
    }
    return window['__dbbAngularDashboardElementComponents'];
}
function updateDef(component, def) {
    const r = angularDefinitionProviders();
    if (!r.has(component)) {
        r.set(component, { ...def, properties: [] });
    }
    else {
        r.set(component, merge(r.get(component), def));
    }
    const e = angularDashboardElementComponents();
    if (!e.includes(component)) {
        e.push(component);
    }
}
function updateProperty(provider, property) {
    const r = angularDefinitionProviders();
    if (!r.has(provider)) {
        r.set(provider, { properties: [property] });
    }
    else {
        const elemDef = r.get(provider);
        const propIndex = elemDef.properties.findIndex((p) => p.name === property.name);
        if (propIndex >= 0) {
            merge(elemDef.properties[propIndex], property);
        }
        else {
            elemDef.properties.push(property);
        }
    }
}
function AngularPropertiesElement(component) {
    angularPropertiesComponents().push(component);
}
function AngularDashboardElement(def) {
    return function (component) {
        updateDef(component, def);
    };
}
function Describe(propertyDescription) {
    return function (target, propertyName) {
        if (typeof propertyDescription === 'string') {
            // the rest has to be defined somewhere else
            updateProperty(target.constructor, {
                name: propertyName,
                description: propertyDescription,
                attributeName: undefined,
                type: undefined,
            });
        }
        else {
            updateProperty(target.constructor, {
                ...propertyDescription,
                name: propertyDescription.name ?? propertyName,
                description: propertyDescription.description,
                attributeName: propertyDescription.attributeName,
                type: propertyDescription.type,
            });
        }
    };
}

var _DashboardElementRegistry_registrationListeners, _DashboardElementRegistry_elements;
class DashboardElementRegistry {
    constructor() {
        _DashboardElementRegistry_registrationListeners.set(this, new Array());
        _DashboardElementRegistry_elements.set(this, new Array());
    }
    define(name, constructor, options, description) {
        customElements.define(name, constructor, options);
        __classPrivateFieldGet(this, _DashboardElementRegistry_elements, "f").push({ name, constructor, description });
        __classPrivateFieldGet(this, _DashboardElementRegistry_registrationListeners, "f").forEach((cb) => cb(description));
    }
    getDescriptions() {
        return __classPrivateFieldGet(this, _DashboardElementRegistry_elements, "f").map((element) => element.description);
    }
    getDescription(name) {
        return __classPrivateFieldGet(this, _DashboardElementRegistry_elements, "f").find((element) => element.name === name)?.description;
    }
    getProperty(elementName, propertyName) {
        return elementName && propertyName
            ? this.getDescription(elementName)?.properties.find((property) => property.name === propertyName)
            : undefined;
    }
    registerElementDefinitionCallback(cb) {
        __classPrivateFieldGet(this, _DashboardElementRegistry_registrationListeners, "f").push(cb);
    }
}
_DashboardElementRegistry_registrationListeners = new WeakMap(), _DashboardElementRegistry_elements = new WeakMap();
if (!window.dashboardElements) {
    window.dashboardElements = new DashboardElementRegistry();
}
const dashboardElements = window.dashboardElements;

/**
 * EventEmitters and CustomEvents are very similar, but custom events can bubble up and don't need listeners to fire.
 * EventEmitters need a subscription to properly work and need to be emitted from component to component,
 * thus making them higher-maintenance, but also more easily traceable.
 *
 * Emitted events can also be listened to as custom events. If an angular component is created via document.create or
 * Renderer2, and you add an EventListener to it, this automatically creates a subscription.
 * Similarly, you can use angular mechanisms to listen to custom events.
 *
 * Use events if you want them to bubble up.
 */
class AddBindingDialogEvent extends CustomEvent {
    constructor(property) {
        super(AddBindingDialogEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: property,
        });
    }
}
AddBindingDialogEvent.TYPE = 'addBindingDialog';
class RemoveBindingFromPropertyEvent extends CustomEvent {
    constructor(property) {
        super(RemoveBindingFromPropertyEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: property,
        });
    }
}
RemoveBindingFromPropertyEvent.TYPE = 'removeBindingFromProperty';
class ElementIssueEvent extends CustomEvent {
    constructor(issue) {
        super(ElementIssueEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: {
                issue,
            },
        });
    }
}
ElementIssueEvent.TYPE = 'elementError';
class PropertyChangeEvent extends CustomEvent {
    constructor(property, index, value) {
        super(PropertyChangeEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: {
                property,
                index,
                value,
            },
        });
    }
}
PropertyChangeEvent.TYPE = 'propertyChange';
class PropertyStructureChangeEvent extends CustomEvent {
    constructor(psc) {
        super(PropertyStructureChangeEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: psc,
        });
    }
}
PropertyStructureChangeEvent.TYPE = 'propertyStructureChange';
class BindingRemovedEvent extends CustomEvent {
    constructor(property) {
        super(BindingRemovedEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: property,
        });
    }
}
BindingRemovedEvent.TYPE = 'bindingRemoved';
class BindingValueChangedEvent extends CustomEvent {
    constructor(bindingChange) {
        super(BindingValueChangedEvent.TYPE, {
            bubbles: false,
            composed: true,
            detail: bindingChange,
        });
    }
}
BindingValueChangedEvent.TYPE = 'bindingValueChanged';
class HelpSystemEvent extends CustomEvent {
    constructor(show) {
        super(HelpSystemEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: {
                show,
            },
        });
    }
}
HelpSystemEvent.TYPE = 'helpSystem';
class WaitingForBindingValueEvent extends CustomEvent {
    constructor(isWaiting, property) {
        super(WaitingForBindingValueEvent.TYPE, {
            bubbles: true,
            composed: true,
            detail: {
                isWaiting,
                property,
            },
        });
    }
}
WaitingForBindingValueEvent.TYPE = 'waitingForBindingValue';

/**
 * Generated bundle index. Do not edit.
 */

export { AddBindingDialogEvent, AngularDashboardElement, AngularPropertiesElement, BindingRemovedEvent, BindingValueChangedEvent, DashboardElementRegistry, Describe, ElementCategory, ElementIssueEvent, HelpSystemEvent, PropertyChangeEvent, PropertyStructureChangeEvent, RemoveBindingFromPropertyEvent, WaitingForBindingValueEvent, angularDashboardElementComponents, angularDefinitionProviders, angularPropertiesComponents, dashboardElements };
